import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Card,
  Typography,
  Chip,
  TextField,
  MenuItem,
  IconButton,
  LinearProgress,
  Button,
} from "@mui/material";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  Legend,
} from "recharts";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { InputAdornment } from "@mui/material";

import {
  WifiOff,
  AccessTime,
  HelpOutline,
  Search,
  MoreVert,
  Wifi,
  Http,
  Router,
} from "@mui/icons-material";
import { Pagination } from "@mui/material";

const stats = [
  {
    title: "Online Devices",
    value: 847,
    countColor: "#16A34A",
    bg: "#DCFCE7",
    icon: <Wifi sx={{ color: "#16A34A" }} />,
    detail: "+12  - last hour",
    detailColor: "#16A34A",
  },
  {
    title: "Offline Devices",
    value: 23,

    countColor: "#DC2626",
    bg: "#FEE2E2",
    icon: <WifiOff sx={{ color: "#DC2626" }} />,
    detail: "+3 - last hour",
    detailColor: "#DC2626",
  },
  {
    title: "Dormant Devices",
    value: 56,

    countColor: "#F59E0B",
    bg: "#FEF3C7",
    icon: <AccessTime sx={{ color: "#F59E0B" }} />,
    detail: "No change",
    detailColor: "#6B7280",
  },
  {
    title: "Unknown Status",
    value: 8,

    countColor: "#9CA3AF",
    bg: "#F3F4F6",
    icon: <HelpOutline sx={{ color: "#9CA3AF" }} />,
    detail: "-2 - last hour",
    detailColor: "#9CA3AF",
  },
];

const timelineData = [
  { hour: "23h ago", online: 820, offline: 20, dormant: 50, unknown: 5 },
  { hour: "20h ago", online: 825, offline: 22, dormant: 48, unknown: 6 },
  { hour: "16h ago", online: 830, offline: 18, dormant: 47, unknown: 6 },
  { hour: "12h ago", online: 835, offline: 21, dormant: 45, unknown: 4 },
  { hour: "8h ago", online: 840, offline: 19, dormant: 44, unknown: 3 },
  { hour: "4h ago", online: 845, offline: 23, dormant: 43, unknown: 4 },
  { hour: "0h ago", online: 847, offline: 23, dormant: 56, unknown: 8 },
];

const devices = [
  {
    uid: "DVC-AQ-000123",
    name: "Air Quality Sensor",
    status: "Online",
    lastSeen: "2 minutes ago",
    type: "MQTT",
    rssi: 68,
    snr: 12,
    gateway: "GW-01",
    ip: "192.168.1.45",
  },
  {
    uid: "DVC-TMP-000456",
    name: "Temperature Sensor",
    status: "Online",
    lastSeen: "5 minutes ago",
    type: "LoRaWAN",
    rssi: 82,
    snr: 8,
    gateway: "GW-03",
    ip: "-",
  },
  {
    uid: "DVC-PLC-00789",
    name: "PLC Controller",
    status: "Offline",
    lastSeen: "26 minutes ago",
    type: "HTTP",
    rssi: null,
    snr: null,
    gateway: "-",
    ip: "10.0.2.154",
  },
];

const statusColor = {
  Online: "success",
  Offline: "error",
  Dormant: "warning",
  Unknown: "default",
};

const connectionIcon = (type) => {
  switch (type) {
    case "MQTT":
      return <Wifi fontSize="small" />;
    case "HTTP":
      return <Http fontSize="small" />;
    default:
      return <Router fontSize="small" />;
  }
};

export default function Connectivity() {
  const [page, setPage] = React.useState(1);
  const totalPages = 3; // change later if needed
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 1024);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isMobile]);

  const [status, setStatus] = useState("All Status");
  const [eventType, setEventType] = useState("All Connection Types");
  const [site, setSite] = useState("All Sites");
  const [search, setSearch] = useState("");
  const [filteredLogs, setFilteredLogs] = useState(devices);

  useEffect(() => {
    if (!devices || devices.length === 0) {
      setFilteredLogs([]);
      return;
    }

    let data = [...devices];

    if (search.trim() !== "") {
      const q = search.toLowerCase();
      data = data.filter(
        (log) =>
          log.uid?.toLowerCase().includes(q) ||
          log.name?.toLowerCase().includes(q) ||
          log.gateway?.toLowerCase().includes(q),
      );
    }

    if (status !== "All Status") {
      data = data.filter((log) => log.status === status);
    }

    if (eventType !== "All Connection Types") {
      data = data.filter((log) => log.type === eventType);
    }

    if (site !== "All Sites") {
      data = data.filter((log) => log.site === site);
    }

    setFilteredLogs(data);
  }, [status, eventType, site, search]);

  return (
    <Box p={isMobile ? 1 : 3} mt={isMobile ? "2rem" : 0}>
      {/* ===== STATS CARDS ===== */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 3, // spacing between cards
          // justifyContent: "center", // center items horizontally
        }}
      >
        {stats.map((card, index) => (
          <Box
            key={index}
            sx={{
              flex: "1 1 150px", // grow, shrink, base width
              maxWidth: isMobile ? "280px" : "200px", // responsive max width
              minWidth: "150px",
            }}
          >
            <Card
              sx={{
                p: 3,
                position: "relative",
                borderRadius: "16px",
                boxShadow: "0px 4px 16px rgba(0,0,0,0.06)",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <Box display="flex" justifyContent="space-between">
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: "12px",
                    backgroundColor: card.bg,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {card.icon}
                </Box>

                <Typography
                  sx={{
                    color: card.countColor,
                    fontWeight: 600,
                    fontSize: "14px",
                  }}
                >
                  {card.count}
                </Typography>
              </Box>

              <Box>
                <Typography
                  sx={{
                    mt: 2,
                    color: "#6b7280",
                    fontSize: "14px",
                  }}
                >
                  {card.title}
                </Typography>

                <Typography
                  sx={{
                    mt: 0.5,
                    fontSize: "22px",
                    fontWeight: 700,
                    color: "#111827",
                  }}
                >
                  {card.value}
                </Typography>

                <Typography
                  sx={{
                    mt: 0.5,
                    position: "absolute",
                    top: 12,
                    right: 12,
                    fontSize: "12px",
                    fontWeight: 500,
                    whiteSpace: "nowrap",
                    color: card.detailColor,
                  }}
                >
                  {card.detail}
                </Typography>
              </Box>
            </Card>
          </Box>
        ))}
      </Box>

      <Card
        sx={{
          mt: 3,
          mb: 4,
          p: 3,
          borderRadius: "14px",
          boxShadow: "4px 4px 16px rgba(0,0,0,0.06)",
        }}
      >
        <Typography fontWeight={600} mb={2}>
          Connection Status Timeline (Last 24 Hours)
        </Typography>

        <Box sx={{ width: "100%", height: 280 }}>
          <ResponsiveContainer>
            <AreaChart
              data={timelineData}
              margin={{ top: 10, right: 5, left: -5, bottom: 10 }}
            >
              <defs>
                <linearGradient id="onlineFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#16A34A" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="#16A34A" stopOpacity={0.05} />
                </linearGradient>
              </defs>

              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="hour" />
              <YAxis />
              <ChartTooltip />
              <Legend
                verticalAlign="bottom"
                align="center"
                wrapperStyle={{
                  paddingTop: 20,
                }}
              />

              <Area
                type="monotone"
                dataKey="online"
                stroke="#16A34A"
                fill="url(#onlineFill)"
                name="Online"
              />
              <Area
                type="monotone"
                dataKey="offline"
                stroke="#DC2626"
                fill="#FEE2E2"
                name="Offline"
              />
              <Area
                type="monotone"
                dataKey="dormant"
                stroke="#F59E0B"
                fill="#FEF3C7"
                name="Dormant"
              />
              <Area
                type="monotone"
                dataKey="unknown"
                stroke="#9CA3AF"
                fill="#F3F4F6"
                name="Unknown"
              />
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      </Card>

      {/* ================= FILTER BAR ================= */}
      <Card
        sx={{
          mb: 2,
          p: 2,
          borderRadius: 2,
          boxShadow: "0px 2px 12px rgba(0,0,0,0.04)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2, // spacing between items
            alignItems: "center",
          }}
        >
          {/* Search */}
          <Box sx={{ flex: "1 1 100%", md: { flex: "1 1 33%" } }}>
            <TextField
              fullWidth
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search devices by UID, name, or gateway..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Status Filter */}
          <Box sx={{ flex: "1 1 100%", md: { flex: "1 1 16.6%" } }}>
            <TextField
              select
              fullWidth
              size="small"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <MenuItem value="All Status">All Status</MenuItem>
              <MenuItem value="Online">Online</MenuItem>
              <MenuItem value="Offline">Offline</MenuItem>
              <MenuItem value="Dormant">Dormant</MenuItem>
            </TextField>
          </Box>

          {/* Connection Type Filter */}
          <Box sx={{ flex: "1 1 100%", md: { flex: "1 1 16.6%" } }}>
            <TextField
              select
              fullWidth
              size="small"
              value={eventType}
              onChange={(e) => setEventType(e.target.value)}
            >
              <MenuItem value="All Connection Types">
                All Connection Types
              </MenuItem>
              <MenuItem value="MQTT">MQTT</MenuItem>
              <MenuItem value="LoRaWAN">LoRaWAN</MenuItem>
              <MenuItem value="HTTP">HTTP</MenuItem>
            </TextField>
          </Box>

          {/* Site Filter */}
          <Box sx={{ flex: "1 1 50%", md: { flex: "1 1 16.6%" } }}>
            <TextField
              select
              fullWidth
              size="small"
              value={site}
              onChange={(e) => setSite(e.target.value)}
            >
              <MenuItem value="All Sites">All Sites</MenuItem>
              <MenuItem value="site1">Site 1</MenuItem>
              <MenuItem value="site2">Site 2</MenuItem>
            </TextField>
          </Box>

          {/* Reset Filter Button */}
          <Box
            sx={{
              flex: "1 1 100%",
              md: { flex: "1 1 16.6%" },
              display: "flex",
              justifyContent: { xs: "flex-start", md: "flex-end" },
            }}
          >
            <Button
              variant="contained"
              sx={{
                backgroundColor: "blue",
                color: "white",
                height: "40px",
                textTransform: "none",
              }}
              onClick={() => {
                setStatus("All Status");
                setEventType("All Connection Types");
                setSite("All Sites");
                setSearch("");
              }}
            >
              Reset Filter
            </Button>
          </Box>
        </Box>
      </Card>

      {/* ================= DEVICE TABLE (DESKTOP) ================= */}
      <TableContainer
        component={Paper}
        sx={{
          display: { xs: "none", md: "block" },
          borderRadius: 3,
          boxShadow: "0px 2px 12px rgba(0,0,0,0.06)",
        }}
      >
        <Table
          size="small"
          sx={{
            "& th": {
              fontSize: "12.5px",
              fontWeight: 500,
              py: 1, // header height smaller
            },
            "& td": {
              fontSize: "10.5px",
              py: 1, // row height smaller
            },
          }}
        >
          <TableHead sx={{ backgroundColor: "#F9FAFB" }}>
            <TableRow>
              <TableCell>
                <b>Device UID</b>
              </TableCell>
              <TableCell>
                <b>Status</b>
              </TableCell>
              <TableCell>
                <b>Last Seen</b>
              </TableCell>
              <TableCell>
                <b>Connection Type</b>
              </TableCell>
              <TableCell>
                <b>Signal Quality</b>
              </TableCell>
              <TableCell>
                <b>Gateway</b>
              </TableCell>
              <TableCell>
                <b>IP Address</b>
              </TableCell>
              <TableCell align="right">
                <b>Actions</b>
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredLogs.map((d) => (
              <TableRow key={d.uid} hover>
                {/* Device */}
                <TableCell>
                  <Typography fontWeight={600}>{d.uid}</Typography>
                  <Typography fontSize={10} color="text.secondary">
                    {d.name}
                  </Typography>
                </TableCell>

                {/* Status */}
                <TableCell>
                  <Chip
                    label={d.status}
                    size="small"
                    color={statusColor[d.status]}
                  />
                </TableCell>

                {/* Last Seen */}
                <TableCell>
                  <Typography>{d.lastSeen}</Typography>
                  <Typography fontSize={11} color="text.secondary">
                    08:12:00 UTC
                  </Typography>
                </TableCell>

                {/* Connection */}
                <TableCell>
                  <Chip
                    size="small"
                    icon={connectionIcon(d.type)}
                    label={d.type}
                  />
                </TableCell>

                {/* Signal */}
                <TableCell sx={{ minWidth: 160 }}>
                  {d.rssi ? (
                    <>
                      <Typography fontSize={11}>RSSI: -{d.rssi} dBm</Typography>
                      <LinearProgress
                        variant="determinate"
                        value={100 - d.rssi}
                        sx={{ height: 6, borderRadius: 3, my: 0.5 }}
                      />
                      <Typography fontSize={11} color="text.secondary">
                        SNR: {d.snr} dB
                      </Typography>
                    </>
                  ) : (
                    <Typography color="text.secondary">N/A</Typography>
                  )}
                </TableCell>

                {/* Gateway */}
                <TableCell>
                  <Typography>{d.gateway}</Typography>
                </TableCell>

                {/* IP */}
                <TableCell>
                  <Typography color="primary">{d.ip}</Typography>
                </TableCell>

                {/* Actions */}
                <TableCell align="right">
                  <IconButton size="small">
                    <MoreVert />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ================= DEVICE CARDS (MOBILE) ================= */}
      <Box sx={{ display: { xs: "block", md: "none" }, mt: 2 }}>
        {filteredLogs.map((d) => (
          <Card key={d.uid} sx={{ mb: 2, borderRadius: 2, p: 2 }}>
            {/* Header */}
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box>
                <Typography fontWeight={600}>{d.uid}</Typography>
                <Typography fontSize={12} color="text.secondary">
                  {d.name}
                </Typography>
              </Box>
              <IconButton size="small">
                <MoreVert />
              </IconButton>
            </Box>

            {/* Status */}
            <Box mt={1}>
              <Chip
                size="small"
                label={d.status}
                color={statusColor[d.status]}
              />
            </Box>

            {/* Info */}
            <Box mt={1}>
              <Typography fontSize={13}>⏱ {d.lastSeen}</Typography>
              <Typography fontSize={13}>🌐 {d.type}</Typography>
            </Box>

            {/* Signal */}
            <Box mt={1}>
              {d.rssi ? (
                <>
                  <Typography fontSize={12}>RSSI: -{d.rssi} dBm</Typography>
                  <LinearProgress
                    variant="determinate"
                    value={100 - d.rssi}
                    sx={{ height: 6, borderRadius: 3 }}
                  />
                  <Typography fontSize={12} color="text.secondary">
                    SNR: {d.snr} dB
                  </Typography>
                </>
              ) : (
                <Typography color="text.secondary">Signal: N/A</Typography>
              )}
            </Box>

            {/* Gateway + IP */}
            <Box mt={1}>
              <Typography fontSize={13}>Gateway: {d.gateway}</Typography>
              <Typography fontSize={13} color="primary">
                IP: {d.ip}
              </Typography>
            </Box>
          </Card>
        ))}
      </Box>
      <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
        <Pagination
          count={totalPages}
          page={page}
          size="small"
          shape="rounded"
          siblingCount={1}
          boundaryCount={1}
          onChange={(event, value) => setPage(value)}
        />
      </Box>
    </Box>
  );
}
